<template src="./Payment.html"></template>

<script>
import moment from "moment-timezone";
import {  getAnalystsWithPaymentInfo, getAnalystPaymentInfo,createPayment } from "../../../services";
import { getStatusServiceChip, getServiceStatus } from "../../../utils/computeds";
import { isNumber, isVisitService } from "../../../utils";
import Analysts from "../../../components/Forms/Analysts/Analysts";

const todayInBogota = () => moment().tz("America/Bogota").format("YYYY-MM-DD");

export default {
  name: "Payment",
  components: { Analysts },
  data: () => ({
    search: {
      analystId: null,
      finalizedAt: [],
    },
    analysts: [],
    analystActive: null,
    activeTabIndex: null,
    analystServices: [],
    analystServicesSkip: 0,
    analystServicesTake: 100,
    analystServicesTotal: 0,
    menu: false,
    dateModal: false,
    invoiceDate: todayInBogota(),
    invoiceNumber: '',
    observation: '',
    checkedAll: false,
    manualTotal: null,
  }),

  created() {
    this.searchAnalysts();
  },
  methods: {
    isNumber,
    isVisitService,
    // Viáticos (process.extraCost) go with the process's visit service, so they are paid to
    // whoever did the visit and only in the payment that includes that service.
    computeViaticos(services) {
      const seenProcessIds = new Set();
      let total = 0;
      let count = 0;
      for (const s of services) {
        const processId = s.process?.id;
        const extraCost = Number(s.process?.extraCost) || 0;
        if (!isVisitService(s) || processId == null || extraCost <= 0 || seenProcessIds.has(processId)) continue;
        seenProcessIds.add(processId);
        total += extraCost;
        count += 1;
      }
      return { total, count };
    },
    async searchAnalysts() {
      try {
        const params = {
          analystId: this.search.analystId || null,
          startDate: this.search.finalizedAt?.[0] || null,
          endDate: this.search.finalizedAt?.[1] || null,
        };

        const analysts = await getAnalystsWithPaymentInfo(params);
        this.analysts = analysts.map((analyst) => {
          analyst.fullname = `${analyst.name} ${analyst.lastName}`;
          return analyst;
        });

        if (this.search.analystId) {
          const targetAnalyst = this.analysts.find(analyst => analyst.id === this.search.analystId);
          if (targetAnalyst) {
            const targetIndex = this.analysts.findIndex(analyst => analyst.id === this.search.analystId);
            this.selectAnalyst(targetAnalyst, targetIndex);
          } else {
            this.analystServices = [];
            this.analystActive = null;
            this.activeTabIndex = null;
          }
        } else if (this.analysts.length > 0) {
          let analystToSelect;
          let indexToSelect;

          if (this.analystActive && this.analysts.find(analyst => analyst.id === this.analystActive.id)) {
            analystToSelect = this.analysts.find(analyst => analyst.id === this.analystActive.id);
            indexToSelect = this.analysts.findIndex(analyst => analyst.id === this.analystActive.id);
          } else {
            analystToSelect = this.analysts[0];
            indexToSelect = 0;
          }
          this.selectAnalyst(analystToSelect, indexToSelect);
        } else {
          this.analystServices = [];
          this.analystActive = null;
          this.activeTabIndex = null;
        }
      } catch (err) {
        console.log("Error on search analysts:", err);
        this.analysts = [];
        this.analystServices = [];
        this.analystActive = null;
        this.activeTabIndex = null;
      }
    },
    async paySelected() {
      const selected = this.analystServices.filter(s => s.checked);
      if (!selected.length) return;

      const payload = {
        analystId: this.analystActive?.id,
        services: selected.map(s => s.id),
        paymentDate: this.invoiceDate,
        invoiceNumber: this.invoiceNumber,
        total: this.totalSelected,
        observation: this.observation || '',
        servicesSummary: this.groupedSelectedServices,
      };
      try {
        await createPayment(payload);
        this.$emit('payment-success');
        this.$swal({
          text: "Pago realizado correctamente.",
          buttons: false,
          icon: "success",
          timer: 3000,
        });
        this.invoiceDate = todayInBogota();
        this.invoiceNumber = '';
        this.observation = '';
        await this.searchAnalysts();
      } catch (error) {
        console.error('Error al crear el pago:', error);
        this.$swal({
          text: error.message ?? "Ocurrió un error, intenta más tarde.",
          buttons: false,
          icon: "error",
          timer: 3000,
        });
      }
    },
    async selectAnalyst(analyst, index = null) {
      this.analystActive = analyst;
      this.analyst = analyst;
      this.analystServicesSkip = 0;
      this.checkedAll = false;

      if (index === null) {
        index = this.analysts.findIndex(a => a.id === analyst.id);
      }
      this.activeTabIndex = index;

      await this.loadAnalystServices(true);
    },
    async loadAnalystServices(reset = false) {
      if (!this.analystActive) return;
      try {
        const params = {
          skip: this.analystServicesSkip,
          take: this.analystServicesTake,
          startDate: this.search.finalizedAt?.[0] || null,
          endDate: this.search.finalizedAt?.[1] || null,
        };
        const response = await getAnalystPaymentInfo(this.analystActive.id, params);
        if (response && Array.isArray(response.data)) {
          const services = response.data.map(service => ({ ...service, checked: false }));
          if (reset) {
            this.analystServices = services;
          } else {
            this.analystServices = this.analystServices.concat(services);
          }
          this.analystServicesTotal = response.total || this.analystServices.length;
        } else if (Array.isArray(response)) {
          const services = response.map(service => ({ ...service, checked: false }));
          if (reset) {
            this.analystServices = services;
          } else {
            this.analystServices = this.analystServices.concat(services);
          }
          this.analystServicesTotal = this.analystServices.length;
        } else {
          this.analystServices = [];
          this.analystServicesTotal = 0;
        }
      } catch (err) {
        this.analystServices = [];
        this.analystServicesTotal = 0;
        console.log("Error retrieving payment information:", err);
      }
    },
    async loadMoreAnalystServices() {
      this.analystServicesSkip += this.analystServicesTake;
      await this.loadAnalystServices();
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
    },
    checkAll(e) {
      e.cancelBubble = true;
      this.manualTotal = null;

      if (this.checkedAll) {
        for (const service of this.analystServices) {
          service.checked = true;
        }
      } else {
        for (const service of this.analystServices) {
          service.checked = false;
        }
      }
    },
    checkService(service) {
      this.manualTotal = null;
      if (!service.checked) {
        this.checkedAll = false;
      } else {
        const allChecked = this.analystServices.every(s => s.checked);
        this.checkedAll = allChecked;
      }
    },
  },
  computed: {
    getStatusServiceChip,
    getServiceStatus,
    groupedSelectedServices() {
      const grouped = {};
      const checked = (this.analystServices || []).filter(s => s.checked);

      checked.forEach(s => {
        if (!grouped[s.name]) {
          grouped[s.name] = { cantidad: 0, valor: 0 };
        }
        grouped[s.name].cantidad += 1;
        grouped[s.name].valor += Number(s.cost) || 0;
      });

      const result = Object.entries(grouped).map(([name, data]) => ({
        name,
        cantidad: data.cantidad,
        valor: `$${data.valor.toLocaleString('es-CO')}`
      }));

      const { total: viaticosTotal, count: viaticosCount } = this.computeViaticos(checked);
      if (viaticosTotal > 0) {
        result.push({
          name: 'Viáticos',
          cantidad: viaticosCount,
          valor: `$${viaticosTotal.toLocaleString('es-CO')}`
        });
      }

      return result;
    },
    totalSelected: {
      get() {
        if (this.manualTotal !== null) return this.manualTotal;
        const checked = (this.analystServices || []).filter(s => s.checked);
        const servicesTotal = checked.reduce((acc, s) => acc + (Number(s.cost) || 0), 0);
        const { total: viaticosTotal } = this.computeViaticos(checked);
        return servicesTotal + viaticosTotal;
      },
      set(value) {
        this.manualTotal = value === '' ? null : value;
      },
    },
    hasSelected() {
      return (this.analystServices || []).some(s => s.checked);
    },
  },
};
</script>

<style src="./Payment.scss" lang="scss"></style>
